/**
 * Reusable SSE client wrapper using the native EventSource API.
 *
 * Normalizes incoming events into typed discriminated unions
 * and provides a clean close() handle to tear down the connection.
 *
 * Real endpoint: GET /v1/gamification/xp-gains/stream?token=<jwt>
 * All events arrive as named event type "xp-event" with a discriminated
 * `source.type` field: "daily-login" | "xp-gain" | "level-up".
 */

import type { components } from '~/api/openapi';
import { safeJsonParse } from '~/lib/safe-json-parse';

export type XpEventDto = components['schemas']['XpEventDto'];
export type GamificationStatsDto = components['schemas']['GamificationStatsDto'];
export type SseTokenDto = components['schemas']['SseTokenDto'];

/** The named SSE event type the backend sends for all XP events. */
export const SSE_XP_EVENT = 'xp-event' as const;

export type SseXpEvent =
  | { type: typeof SSE_XP_EVENT; event: XpEventDto }
  | { type: 'unknown'; raw: string };

export type CreateSseClientOptions = {
  /** Full SSE URL including token query param. */
  url: string;
  /** Called for every parsed XP event. */
  onEvent: (event: SseXpEvent) => void;
  /** Called when the connection opens. */
  onOpen?: () => void;
  /** Called on connection errors (EventSource auto-retries by default). */
  onError?: (error: Event) => void;
};

export type SseClient = {
  /** Close the EventSource connection and stop receiving events. */
  close: () => void;
};

/**
 * Create an SSE connection to the XP gains stream.
 *
 * Uses the native `EventSource` API, which automatically reconnects on
 * transient network errors and sends `Last-Event-Id` on reconnect.
 * Call `close()` on the returned handle to permanently shut down.
 */
export function createSseClient(options: CreateSseClientOptions): SseClient {
  const { url, onEvent, onOpen, onError } = options;

  const source = new EventSource(url);

  source.onopen = () => {
    onOpen?.();
  };

  source.onerror = err => {
    onError?.(err);
  };

  const handleXpEvent = (ev: MessageEvent) => {
    const parsed = safeJsonParse<XpEventDto | null>(ev.data, null, { captureParseErrors: false });
    if (!parsed?.source?.type) {
      onEvent({ type: 'unknown', raw: ev.data });
      return;
    }

    onEvent({ type: SSE_XP_EVENT, event: parsed });
  };

  source.addEventListener(SSE_XP_EVENT, handleXpEvent);

  source.onmessage = (ev: MessageEvent) => {
    handleXpEvent(ev);
  };

  return {
    close() {
      source.removeEventListener(SSE_XP_EVENT, handleXpEvent);
      source.onmessage = null;
      source.close();
    },
  };
}
